import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGridPremium } from '@mui/x-data-grid-premium';

const TerminalsAccordion = ({ controlType, defaultExpanded, terminals }) => {
  const [isExpanded, setExpanded] = useState(defaultExpanded);
  const isSingleControl = R.equals('SINGLE', controlType);

  const renderIcon = status =>
    status ? (
      <Box
        sx={{
          backgroundColor: 'success.dark',
          borderRadius: '50%',
          height: 14,
          mr: 0.5,
          position: 'relative',
          width: 14,
          zIndex: 0,
        }}
      >
        <CheckCircleRoundedIcon
          sx={{
            color: 'success.light',
            height: 20,
            left: -3,
            position: 'absolute',
            top: -3,
            width: 20,
          }}
        />
      </Box>
    ) : (
      <Box
        sx={{
          backgroundColor: 'error.main',
          borderRadius: '50%',
          height: 14,
          mr: 0.5,
          position: 'relative',
          width: 14,
          zIndex: 0,
        }}
      >
        <ErrorIcon
          sx={{
            color: 'error.light',
            height: 20,
            left: -3,
            position: 'absolute',
            top: -3,
            width: 20,
          }}
        />
      </Box>
    );

  const columns = [
    {
      field: 'terminalShortName',
      headerName: 'Location',
      width: 200,
    },
    {
      field: 'remainingVolume',
      headerName: 'Remaining Volume',
      width: 150,
    },
    {
      align: 'center',
      field: 'terminalLoadingStatus',
      headerName: 'Loading Status',
      renderCell: params => {
        return renderIcon(params.row.terminalLoadingStatus);
      },
      width: 130,
    },
  ];

  return (
    <Accordion expanded={isExpanded} onChange={() => setExpanded(!isExpanded)}>
      <AccordionSummary aria-controls="terminals-content" expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1}>
          <Typography variant="body2">Terminals</Typography>
          {!!terminals.length && !isSingleControl && (
            <Chip label={terminals.length} type="accordion" variant="badge" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption">
          To change the Loading status for a terminal,{' '}
          <Link
            href="mailto:atlasshipperservices@magellanlp.com?subject=Terminal Loading Status"
            sx={{ color: 'secondary.main', textDecoration: 'none' }}
          >
            click here to email Magellan Support
          </Link>
        </Typography>
        <Box>
          {terminals.length > 0 ? (
            <DataGridPremium
              autoHeight
              columns={columns}
              getRowId={row => row.terminalId}
              rows={terminals}
              style={{ width: '100%' }}
            />
          ) : (
            <Typography>No Terminals is available.</Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

TerminalsAccordion.propTypes = {
  controlType: PropTypes.string.isRequired,
  defaultExpanded: PropTypes.bool,
  terminals: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

TerminalsAccordion.defaultProps = {
  defaultExpanded: false,
};

export default TerminalsAccordion;
